/**
 * Value Form Modal Component for MDM
 *
 * Purpose: Wrapper component for MetadataModal that handles MDM-specific logic
 * Architecture: Wraps MetadataModal with conflict resolution and MDM value mapping
 * Dependencies: React, Material-UI, MetadataModal, MDM service
 * Author: Development Team
 * Created: 2025-01-31
 * Updated: 2025-02-02 - Refactored to use MetadataModal
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Alert,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
  Popover,
  Autocomplete,
  Link,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { LoadingButton } from '../common/LoadingButton';
import { useQuery } from '@tanstack/react-query';
import { MetadataModal } from '../metadata-modal/MetadataModal';
import { fieldGridSx } from '../form-layout/fieldGrid';
import { useAuth } from '../../context/AuthContext';
import { PermissionKey } from '../../constants/permissionKeys';
import { mdmService } from '../../services/mdmService';
import { MasterDataValue, ConflictData, ConflictType } from '../../types/mdm';
import { FieldDefinition } from '../../services/metadataService';
import { getScopeChipColor } from '../../constants/mdm';
import { ParentValueSelector } from './ParentValueSelector';
import type { FixedField, FormData as MetadataFormData } from '../metadata-form/types';
import { formatAuditUser } from '../../utils/formatting';
import { formatDateTime } from '../../utils/datetime';
import {
  valueChipColors,
  valueChipColorKeys,
  type ValueChipColorKey,
} from '../../theme/orchestrateTheme';
import { SecondaryButton } from '../common/ActionButtons';

/**
 * Chip Color Selector Component
 *
 * Purpose: Small color selector for choosing chip colors from preset palette
 * Displays as a compact button with popover showing all 12 color options
 */
interface ChipColorSelectorProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

const ChipColorSelector: React.FC<ChipColorSelectorProps> = ({ value, onChange, disabled }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (colorKey: ValueChipColorKey | null) => {
    onChange(colorKey);
    handleClose();
  };

  const selectedColor = value ? valueChipColors[value as ValueChipColorKey] : null;

  return (
    <>
      <Tooltip title={value ? `Chip color: ${value}` : 'Select chip color'}>
        <Box
          component="button"
          onClick={handleClick}
          disabled={disabled}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: selectedColor ? selectedColor.main : 'grey.300',
            backgroundColor: selectedColor ? selectedColor.main : 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            '&:hover': {
              opacity: disabled ? 1 : 0.8,
            },
            '&:disabled': {
              opacity: 0.5,
            },
          }}
        >
          {!selectedColor && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: '1px dashed',
                borderColor: 'grey.400',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
                +
              </Typography>
            </Box>
          )}
        </Box>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
            Chip Color
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {/* None option */}
            <Box>
              <Tooltip title="None (default)">
                <Box
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleColorSelect(null);
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: value === null ? 'primary.main' : 'grey.300',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '60%',
                      height: '60%',
                      border: '1px dashed',
                      borderColor: 'grey.400',
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
            {/* Color options */}
            {valueChipColorKeys.map((colorKey) => {
              const color = valueChipColors[colorKey];
              const isSelected = value === colorKey;
              return (
                <Box key={colorKey}>
                  <Tooltip title={colorKey}>
                    <Box
                      component="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleColorSelect(colorKey);
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'transparent',
                        backgroundColor: color.main,
                        cursor: 'pointer',
                        padding: 0,
                        '&:hover': {
                          opacity: 0.8,
                          borderColor: 'primary.light',
                        },
                      }}
                    />
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// Props interface
/**
 * The permission a value form's own pickers offer values for (ORCH-332): what the
 * write uses - mdm.edit, or "Add new"'s mdm.inline_create for someone without it.
 */
export const valueFormPickerPermission = (
  creatorMode: boolean,
  canEditMdm: boolean
): PermissionKey =>
  creatorMode && !canEditMdm ? PermissionKey.MDM_INLINE_CREATE : PermissionKey.MDM_EDIT;

export interface ValueFormModalProps {
  open: boolean;
  onClose: () => void;
  value?: MasterDataValue;
  fielddef_id: string;
  tenant_id: string;
  fieldDefinition?: FieldDefinition;
  onSuccess: (value: MasterDataValue) => void;
  readOnly?: boolean; // If true, modal opens in view-only mode (no editing allowed)
  /**
   * ORCH-128: When true, renders the modal in a constrained "creator mode" for
   * inline MDM value creation from a form field. Hides admin-only affordances
   * (chip color, scope banner, alias input) and replaces the ConflictDialog
   * reactivation flow with a plain inline error on 409.
   * Must be subtractive-only — no second submit path or validation branch.
   */
  creatorMode?: boolean;
  /**
   * ORCH-115: Optional callback invoked when the user clicks "Manage aliases"
   * in edit mode. Receives the value being edited so the parent can open the
   * AliasManagerDrawer for it. The modal is closed by the time this fires
   * (no nested modal/drawer).
   */
  onManageAliases?: (value: MasterDataValue) => void;
}

/**
 * Conflict Resolution Dialog
 *
 * Purpose: Displays conflict information and resolution options
 * Features: Reactivate existing, create new, or cancel actions
 */
interface ConflictDialogProps {
  open: boolean;
  conflict: ConflictData;
  onResolve: (action: 'reactivate' | 'create_new' | 'create_override' | 'cancel') => void;
  loading?: boolean;
  /**
   * ORCH-282: whether a CROSS_SCOPE_GLOBAL conflict may offer "Create Override".
   * True on the create path; false on rename - converting an existing LOCAL row
   * to an OVERRIDDEN row in place would change its data lineage, so rename only
   * blocks with a message (per the enhancement doc's chosen approach).
   */
  allowOverride?: boolean;
}

const ConflictDialog: React.FC<ConflictDialogProps> = ({
  open,
  conflict,
  onResolve,
  loading = false,
  allowOverride = true,
}) => {
  // ORCH-282: a GLOBAL value already visible to this tenant collides on name.
  const isCrossScopeGlobal = conflict.conflict_type === ConflictType.CROSS_SCOPE_GLOBAL;
  // Offer to create a tenant override instead of the reactivate/force-create
  // choices that apply to same-tenant duplicates - but only on the create path;
  // rename blocks with no override offer (see allowOverride prop doc).
  const canOfferOverride = isCrossScopeGlobal && allowOverride;

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          {isCrossScopeGlobal ? 'GLOBAL Value Conflict' : 'Duplicate Value Detected'}
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Conflict Message */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          {conflict.message}
        </Alert>

        {/* Existing Value Information */}
        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Existing Value Details
          </Typography>
          <Typography variant="body2">
            Name: <strong>{conflict.existing_value_name || 'Unknown'}</strong>
          </Typography>
          <Typography variant="body2">
            ID: <code>{conflict.existing_value_id}</code>
          </Typography>
          {conflict.existing_global_value_id && (
            <Typography variant="body2">
              Global Value ID: <code>{conflict.existing_global_value_id}</code>
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            {conflict.is_active === false && (
              <Chip label="Inactive" size="small" color="warning" sx={{ mr: 1 }} />
            )}
            {conflict.is_deleted && <Chip label="Deleted" size="small" color="error" />}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={() => onResolve('cancel')} disabled={loading}>
          Cancel
        </SecondaryButton>

        {canOfferOverride && (
          <LoadingButton
            onClick={() => onResolve('create_override')}
            loading={loading}
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
          >
            Create Override
          </LoadingButton>
        )}

        {!isCrossScopeGlobal && conflict.can_reactivate && (
          <LoadingButton
            onClick={() => onResolve('reactivate')}
            loading={loading}
            startIcon={<SuccessIcon />}
            variant="contained"
            color="success"
          >
            Reactivate Existing
          </LoadingButton>
        )}

        {!isCrossScopeGlobal && !conflict.can_reactivate && (
          <LoadingButton
            onClick={() => onResolve('create_new')}
            loading={loading}
            startIcon={<AddIcon />}
            variant="contained"
            color="error"
          >
            Create New Anyway
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

/**
 * Value Form Modal Component
 *
 * Purpose: Wrapper for MetadataModal with MDM-specific behaviors
 * Features: View config fetching, conflict resolution, data mapping
 */
const ValueFormModal: React.FC<ValueFormModalProps> = ({
  open,
  onClose,
  value,
  fielddef_id,
  tenant_id,
  fieldDefinition,
  onSuccess,
  readOnly = false,
  creatorMode = false,
  onManageAliases,
}) => {
  // State management
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [conflict, setConflict] = useState<ConflictData | null>(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState<boolean>(false);
  const [pendingFormData, setPendingFormData] = useState<MetadataFormData | null>(null);
  // ORCH-282: captured at submit time rather than re-derived from `isEdit` when
  // the conflict renders - the parent's `value` prop is not guaranteed stable
  // across the failed-submit re-render, and rename must never offer an override.
  const [conflictWasRename, setConflictWasRename] = useState<boolean>(false);
  const [_combinedFieldKey, setCombinedFieldKey] = useState<number>(0); // Force re-render of combined field
  // ORCH-128: inline error shown in creatorMode instead of ConflictDialog
  const [creatorModeError, setCreatorModeError] = useState<string | null>(null);
  const { can } = useAuth();

  // ORCH-115: aliases entered via the create-mode chip input. Reset on each
  // open so the field starts empty for new values.
  const [pendingAliases, setPendingAliases] = useState<string[]>([]);
  const [aliasInputError, setAliasInputError] = useState<string | null>(null);
  React.useEffect(() => {
    if (open) {
      setPendingAliases([]);
      setAliasInputError(null);
      setCreatorModeError(null);
    }
  }, [open]);

  const isEdit = !!value;
  const isView = readOnly || false;

  // Determine modal title based on mode
  const title = React.useMemo(() => {
    if (isView) return 'View Master Data Value';
    return isEdit ? 'Edit Master Data Value' : 'Create Master Data Value';
  }, [isView, isEdit]);

  // Fetch full value with related fields when in edit mode
  const {
    data: fullValue,
    isLoading: valueLoading,
    error: _valueError,
  } = useQuery({
    queryKey: ['mdm-value', value?.id],
    queryFn: () => mdmService.getValue(value!.id),
    enabled: open && isEdit && !!value?.id,
    staleTime: 0, // Always fetch fresh data when opening edit modal
  });

  // Use fullValue if available, otherwise fall back to value prop
  const valueToUse = isEdit && fullValue ? fullValue : value;

  // Check if field is hierarchical
  const isHierarchical = React.useMemo(() => {
    return (
      fieldDefinition?.hierarchy_config !== null && fieldDefinition?.hierarchy_config !== undefined
    );
  }, [fieldDefinition]);

  // Fetch modal view configuration
  const {
    data: viewConfig,
    isLoading: viewLoading,
    error: _viewError,
  } = useQuery({
    queryKey: ['mdm-modal-view', fielddef_id, tenant_id],
    queryFn: () => mdmService.getModalView(fielddef_id, tenant_id),
    enabled: open,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  /**
   * Map MDM value to MetadataModal initial data format
   *
   * Purpose: Convert MasterDataValue to format expected by MetadataModal
   * Logic: Extracts fixed fields, parent_value_id (for hierarchical fields), and metadata
   * Input: MasterDataValue object
   * Output: MetadataFormData object
   */
  const mapValueToFormData = React.useCallback(
    (mdmValue?: MasterDataValue): Record<string, any> => {
      if (!mdmValue) {
        return {
          name: '',
          is_active: true,
          parent_value_id: null,
          metadata: {},
          chip_color: null,
        };
      }

      return {
        name: mdmValue.name || '',
        is_active: mdmValue.is_active ?? true,
        parent_value_id: mdmValue.parent_value_id || null,
        metadata: mdmValue.meta_data || {},
        chip_color: mdmValue.chip_color || null,
      };
    },
    []
  );

  // Memoize initial data to prevent unnecessary re-renders
  // In edit mode, wait for fullValue to load before setting initialData
  // This prevents the form from resetting when fullValue loads
  const initialFormData = React.useMemo(() => {
    // In create mode, always return empty form data
    if (!isEdit) {
      return mapValueToFormData(undefined);
    }

    // In edit mode, ONLY use fullValue (never use the value prop)
    // This ensures we have complete metadata before initializing the form
    // If fullValue isn't loaded yet, return undefined to show loading state
    if (fullValue) {
      const formData = mapValueToFormData(fullValue);
      return formData;
    }

    // Return undefined to indicate data is still loading
    // MetadataModal will handle this by showing loading state
    return undefined;
  }, [isEdit, fullValue, mapValueToFormData]);

  // Determine scope display
  const getScopeDisplay = () => {
    if (valueToUse?.scope_info?.scope) {
      return valueToUse.scope_info.scope;
    }
    if (!valueToUse) {
      return tenant_id === 'GLOBAL' ? 'GLOBAL' : 'LOCAL';
    }
    if (tenant_id === 'GLOBAL') {
      return 'GLOBAL';
    }
    if (valueToUse.global_value_id) {
      return 'OVERRIDDEN';
    } else {
      return 'LOCAL';
    }
  };

  /**
   * Create fixed fields for name, parent_value_id (if hierarchical), and is_active
   *
   * Purpose: Define core MDM fields that appear before related fields
   * Logic: Creates FixedField definitions; conditionally includes ParentValueSelector for hierarchical fields
   * Output: Array of FixedField objects
   *
   * Note: Value Name, Active, and Chip Color are combined into a single row using a three-column grid
   */
  const fieldSettersRef = React.useRef<{
    setName: ((value: string) => void) | null;
    setIsActive: ((value: boolean) => void) | null;
    setChipColor: ((value: string | null) => void) | null;
    nameValue?: string;
    isActiveValue?: boolean;
    chipColorValue?: string | null;
    nameError?: boolean;
    nameHelperText?: string;
  }>({ setName: null, setIsActive: null, setChipColor: null });

  const fixedFields: FixedField[] = React.useMemo(() => {
    const fields: FixedField[] = [];

    // Individual fields that update formData and provide setters to the combined field
    // These must come first to populate the refs
    fields.push({
      fieldId: 'name',
      label: 'Value Name',
      type: 'text',
      required: true,
      readOnly: false,
      render: (value, onChange, _readOnly, error, helperText) => {
        // Update refs without triggering state updates (to avoid infinite loops)
        fieldSettersRef.current.setName = onChange as (value: string) => void;
        fieldSettersRef.current.nameValue = (value as string) || '';
        fieldSettersRef.current.nameError = error;
        fieldSettersRef.current.nameHelperText = helperText;
        return null; // Rendered in combined field below
      },
    });

    fields.push({
      fieldId: 'chip_color',
      label: '', // Hidden label
      type: 'custom',
      required: false,
      readOnly: false,
      render: (value, onChange, _readOnly) => {
        // Update refs without triggering state updates (to avoid infinite loops)
        fieldSettersRef.current.setChipColor = onChange as (value: string | null) => void;
        fieldSettersRef.current.chipColorValue = value as string | null;
        return null; // Rendered in combined field below
      },
    });

    fields.push({
      fieldId: 'is_active',
      label: 'Active',
      type: 'boolean',
      required: false,
      readOnly: false,
      render: (value, onChange, _readOnly) => {
        // Update refs without triggering state updates (to avoid infinite loops)
        fieldSettersRef.current.setIsActive = onChange as (value: boolean) => void;
        fieldSettersRef.current.isActiveValue = (value as boolean) ?? true;
        return null; // Rendered in combined field below
      },
    });

    // Combined field: Value Name, Active, and Chip Color in a two-column grid
    // This field reads from the individual field values via refs
    // Must come after individual fields so refs are populated
    // This field takes full width and contains the two-column grid
    fields.push({
      fieldId: 'name_active_color_combined',
      label: '', // No label since we have individual labels
      type: 'custom',
      required: false,
      readOnly: false,
      fullWidth: true, // Always span full width regardless of columnLayout
      render: (_value, _onChange, readOnly) => {
        // This renderer will be called, but we'll read from the individual field refs
        // Use key to force re-render when refs update
        return (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ ...fieldGridSx(2), alignItems: 'center' }}>
              {/* Value Name - first column of the shared field grid */}
              <Box>
                <TextField
                  name="name"
                  label="Value Name"
                  value={fieldSettersRef.current.nameValue || ''}
                  onChange={(e) => {
                    fieldSettersRef.current.nameValue = e.target.value;
                    fieldSettersRef.current.setName?.(e.target.value);
                    setCombinedFieldKey((prev) => prev + 1); // Force re-render
                  }}
                  required
                  error={fieldSettersRef.current.nameError}
                  helperText={fieldSettersRef.current.nameHelperText}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: readOnly,
                  }}
                  data-testid="value-name-input"
                />
              </Box>

              {/* Active toggle and Chip Color selector - right column (together) */}
              <Box
                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldSettersRef.current.isActiveValue ?? true}
                      onChange={(e) => {
                        fieldSettersRef.current.isActiveValue = e.target.checked;
                        fieldSettersRef.current.setIsActive?.(e.target.checked);
                        setCombinedFieldKey((prev) => prev + 1); // Force re-render
                      }}
                      disabled={readOnly}
                    />
                  }
                  label="Active"
                />
                {/* Chip color selector is hidden in creatorMode (ORCH-128 §5.3) */}
                {!creatorMode && fieldSettersRef.current.setChipColor && (
                  <ChipColorSelector
                    value={fieldSettersRef.current.chipColorValue || null}
                    onChange={(val) => {
                      fieldSettersRef.current.chipColorValue = val;
                      fieldSettersRef.current.setChipColor?.(val);
                      setCombinedFieldKey((prev) => prev + 1); // Force re-render
                    }}
                    disabled={readOnly}
                  />
                )}
              </Box>
            </Box>
          </Box>
        );
      },
    });

    // ORCH-115: Aliases field. In create mode this is a chip input (Autocomplete
    // freeSolo + multiple) bundled into the POST /values body. In edit mode it
    // shows a read-only summary plus a "Manage aliases ↗" link that opens the
    // AliasManagerDrawer (modal closes; no nested modal/drawer).
    // ORCH-128: aliases are suppressed entirely in creatorMode — inline creation
    // is for the value itself; aliases can be added later via MDM Manager.
    fields.push({
      fieldId: 'aliases_field',
      label: 'Alternate Names',
      type: 'custom',
      required: false,
      readOnly: false,
      fullWidth: true,
      render: (_value, _onChange, fieldReadOnly) => {
        if (creatorMode) return null;
        if (isEdit && valueToUse) {
          const existing = valueToUse.aliases || [];
          const truncated = (valueToUse as any).aliases_truncated;
          const visible = existing.slice(0, 3);
          const overflow = existing.length - visible.length;
          return (
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Alternate Names
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                {existing.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No alternate names yet.
                  </Typography>
                )}
                {visible.map((a) => (
                  <Chip
                    key={a.id}
                    label={a.alias}
                    size="small"
                    variant={a.source === 'global' ? 'outlined' : 'filled'}
                    color={a.source === 'global' ? 'default' : 'primary'}
                  />
                ))}
                {overflow > 0 && <Chip label={`+${overflow}`} size="small" variant="outlined" />}
                {truncated && (
                  <Tooltip title="Open Manage aliases for the full list">
                    <Chip label="…" size="small" variant="outlined" />
                  </Tooltip>
                )}
                {onManageAliases && !isView && (
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                      onManageAliases(valueToUse);
                    }}
                    sx={{ ml: 1, fontSize: '0.875rem' }}
                  >
                    Manage aliases ↗
                  </Link>
                )}
              </Box>
            </Box>
          );
        }
        // Create mode: chip input
        return (
          <Box sx={{ width: '100%' }}>
            <Autocomplete
              multiple
              freeSolo
              options={[] as string[]}
              value={pendingAliases}
              onChange={(_e, newValue) => {
                // Trim, dedupe case-insensitively. Reject empty.
                const seen = new Set<string>();
                const cleaned: string[] = [];
                let dup = false;
                for (const raw of newValue) {
                  const trimmed = String(raw).trim();
                  if (!trimmed) continue;
                  const key = trimmed.toLowerCase();
                  if (seen.has(key)) {
                    dup = true;
                    continue;
                  }
                  seen.add(key);
                  cleaned.push(trimmed);
                }
                setPendingAliases(cleaned);
                setAliasInputError(dup ? 'Duplicate alias spelling ignored.' : null);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      variant="filled"
                      color="primary"
                      label={option}
                      size="small"
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label="Alternate Names"
                  placeholder="Type and press Enter"
                  error={!!aliasInputError}
                  helperText={
                    aliasInputError ||
                    'Press Enter after each spelling. Aliases must not match any value name or other alias visible to your tenant.'
                  }
                  disabled={fieldReadOnly}
                />
              )}
              disabled={fieldReadOnly}
            />
          </Box>
        );
      },
    });

    // Add ParentValueSelector for hierarchical fields
    // Parent Value is required if the selector is showing (level 2 or greater, i.e., has a parent field definition)
    // Only show selector if field has a parent field definition (not root level)
    const hasParentFieldDef = !!fieldDefinition?.hierarchy_config?.parent_fielddef_id;
    if (isHierarchical && hasParentFieldDef) {
      // If selector is showing, it means this is level 2+ (has a parent field definition)
      // Therefore, parent value is required
      const parentRequired = hasParentFieldDef; // Required if parent field definition exists

      fields.push({
        fieldId: 'parent_value_id',
        label: 'Parent Value',
        type: 'custom',
        required: parentRequired,
        readOnly: false,
        render: (value, onChange, readOnly, error, helperText) => (
          <ParentValueSelector
            fieldDefId={fielddef_id}
            fieldDefinition={fieldDefinition}
            tenantId={tenant_id}
            currentValueId={valueToUse?.id}
            value={value as string | null}
            onChange={onChange}
            disabled={readOnly}
            required={parentRequired}
            error={error}
            helperText={helperText}
            label="Parent Value"
          />
        ),
      });
    }

    return fields;
  }, [
    isHierarchical,
    fielddef_id,
    tenant_id,
    valueToUse?.id,
    fieldDefinition,
    // ORCH-115 aliases field deps
    isEdit,
    valueToUse,
    pendingAliases,
    aliasInputError,
    onManageAliases,
    isView,
    onClose,
    // ORCH-128 creatorMode suppresses chip color and aliases
    creatorMode,
  ]);

  /**
   * Handle form submission
   *
   * Purpose: Submit master data value with conflict detection
   * Logic: Calls MDM service create/update, catches 409 conflicts; includes parent_value_id for hierarchical fields
   * Input: Form data from MetadataModal
   * Exceptions: Handles conflict errors by showing ConflictDialog
   */
  const handleSubmit = async (formData: MetadataFormData) => {
    try {
      setSubmitting(true);

      const payload: any = {
        name: formData.name as string,
        is_active: formData.is_active as boolean,
        fielddef_id: fielddef_id,
        tenant_id: tenant_id,
        meta_data: formData.metadata, // Note: MetadataForm uses 'metadata', MDM API uses 'meta_data'
        chip_color: formData.chip_color || null,
      };

      // Include parent_value_id for hierarchical fields
      if (isHierarchical) {
        payload.parent_value_id = formData.parent_value_id || null;
      }

      let result: MasterDataValue;
      if (isEdit && valueToUse) {
        // ORCH-115: PUT does NOT accept aliases. Edit-mode handler must not
        // include them; aliases are managed via AliasManagerDrawer.
        result = await mdmService.updateValue(valueToUse.id, payload);
      } else {
        // ORCH-115: bundle the chip-input aliases into the atomic create.
        if (pendingAliases.length > 0) {
          payload.aliases = pendingAliases;
        }
        result = await mdmService.createValue(payload);
      }

      onSuccess(result);
      onClose();
    } catch (err: any) {
      // In creatorMode: replace ConflictDialog with an inline error (ORCH-128 §5.3)
      if (creatorMode && err.isConflict) {
        const name = (formData.name as string) || 'this name';
        // ORCH-282: creatorMode has no override round-trip, so a cross-scope
        // GLOBAL collision needs its own guidance rather than the generic message.
        if (err.conflict?.conflict_type === ConflictType.CROSS_SCOPE_GLOBAL) {
          setCreatorModeError(
            `A GLOBAL value named "${name}" is already visible to this tenant. To create a tenant override, use the standard create flow.`
          );
        } else {
          setCreatorModeError(`A value named "${name}" already exists. Try a different name.`);
        }
        return;
      }
      // Check for conflict error
      if (err.isConflict && err.conflict) {
        setConflict(err.conflict);
        setConflictWasRename(isEdit);
        setPendingFormData(formData);
        setConflictDialogOpen(true);
      } else {
        // Re-throw other errors to be handled by MetadataModal
        throw err;
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle conflict resolution
   *
   * Purpose: Process user's choice for conflict resolution
   * Logic: Reactivates existing value or creates new based on user choice
   * Input: User's resolution action
   * Exceptions: Displays error if resolution fails
   */
  const handleConflictResolve = async (
    action: 'reactivate' | 'create_new' | 'create_override' | 'cancel'
  ) => {
    if (action === 'cancel') {
      setConflictDialogOpen(false);
      setConflict(null);
      setPendingFormData(null);
      return;
    }

    if (!conflict || !pendingFormData) return;

    try {
      setSubmitting(true);
      let result: MasterDataValue;

      if (action === 'reactivate' && conflict.existing_value_id) {
        // Reactivate the existing value
        result = await mdmService.reactivateValue(conflict.existing_value_id, { is_active: true });
      } else if (action === 'create_override') {
        // ORCH-282: re-submit with global_value_id set to the colliding GLOBAL
        // value so the server creates an OVERRIDDEN row instead of raising again.
        const payload: any = {
          name: pendingFormData.name as string,
          is_active: pendingFormData.is_active as boolean,
          fielddef_id: fielddef_id,
          tenant_id: tenant_id,
          meta_data: pendingFormData.metadata,
          chip_color: pendingFormData.chip_color || null,
          global_value_id: conflict.existing_value_id,
        };

        if (isHierarchical) {
          payload.parent_value_id = pendingFormData.parent_value_id || null;
        }

        result = await mdmService.createValue(payload);
      } else {
        // Force create new (this might still fail if backend doesn't allow)
        const payload: any = {
          name: pendingFormData.name as string,
          is_active: pendingFormData.is_active as boolean,
          fielddef_id: fielddef_id,
          tenant_id: tenant_id,
          meta_data: pendingFormData.metadata, // Note: MetadataForm uses 'metadata', MDM API uses 'meta_data'
          chip_color: pendingFormData.chip_color || null,
          force_create: true, // Flag to bypass duplicate check
        };

        // Include parent_value_id for hierarchical fields
        if (isHierarchical) {
          payload.parent_value_id = pendingFormData.parent_value_id || null;
        }

        result = await mdmService.createValue(payload);
      }

      onSuccess(result);
      onClose();
      setConflictDialogOpen(false);
    } catch (err: any) {
      console.error('Conflict resolution failed:', err);
      // Could show error in UI, but for now just log
    } finally {
      setSubmitting(false);
      setConflict(null);
      setPendingFormData(null);
    }
  };

  // Info text to display in modal — scope/tenant details hidden in creatorMode (ORCH-128 §5.3)
  const infoText = creatorMode ? null : (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
          Scope:
        </Typography>
        <Chip label={getScopeDisplay()} size="small" color={getScopeChipColor(getScopeDisplay())} />
      </Box>
      <Typography variant="body2" component="span">
        <strong>Field Definition:</strong> {fieldDefinition?.label || fielddef_id}
      </Typography>
      <Typography variant="body2" component="span">
        <strong>Tenant:</strong> {tenant_id}
      </Typography>
      {isEdit && valueToUse && (
        <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
          <strong>Created</strong> {formatDateTime(valueToUse.created_at)} <strong>by</strong>{' '}
          {formatAuditUser(valueToUse.created_by)}
        </Typography>
      )}
    </Box>
  );

  const creatorModeTitle = fieldDefinition?.label
    ? `Add New ${fieldDefinition.label}`
    : 'Add New Value';

  // ORCH-332: this form's own list pickers (a vendor's countries) offer what the write
  // allows: mdm.edit, or "Add new"'s mdm.inline_create for someone without it. Passed to
  // the form as an override of its model default (ORCH-366), which would be mdm.edit.
  const pickerPermission = valueFormPickerPermission(creatorMode, can(PermissionKey.MDM_EDIT));

  return (
    <>
      {/* ORCH-128: inline duplicate error shown in creatorMode instead of ConflictDialog */}
      {creatorMode && creatorModeError && (
        <Alert
          severity="error"
          onClose={() => setCreatorModeError(null)}
          sx={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            maxWidth: 480,
          }}
        >
          {creatorModeError}
        </Alert>
      )}
      {/* Main Modal with MetadataModal */}
      <MetadataModal
        open={open}
        onClose={onClose}
        title={creatorMode ? creatorModeTitle : title}
        infoText={infoText}
        size="md"
        mode={isView ? 'view' : isEdit ? 'edit' : 'create'}
        modelId={fielddef_id}
        tenantId={tenant_id}
        pickerPermission={pickerPermission}
        viewConfig={viewConfig}
        initialData={initialFormData || undefined}
        fixedFields={fixedFields}
        showSectionDivider={true}
        sectionTitle="Details"
        columnLayout="double"
        onSubmit={isView ? undefined : handleSubmit}
        onSuccess={() => {}}
        submitButtonText={isEdit ? 'Save' : 'Create'}
        closeButtonText="Close"
        loading={viewLoading || valueLoading || (isEdit && !fullValue)}
        submitting={submitting}
        warnOnUnsavedChanges={!isView}
      />

      {/* Conflict Resolution Dialog */}
      {conflict && (
        <ConflictDialog
          open={conflictDialogOpen}
          conflict={conflict}
          onResolve={handleConflictResolve}
          loading={submitting}
          allowOverride={!conflictWasRename}
        />
      )}
    </>
  );
};

export default ValueFormModal;
